// BaseProjectView Component
/* eslint standard/no-callback-literal:0, react/jsx-handler-names:0, indent: 0 */

import React from 'react'
import _ from 'lodash'
import { RadialProgressBar, ThemeProvider, lightTheme } from '@rodeo-dev/legos'

import app from '../../../app.jsx'
import TagUtil from '../../../lib/tagutil.jsx'
import PageDetail from '../../../theme/page/page_detail/page_detail.jsx'
import ProjectForm from '../../form/project_form/project_form.jsx'
import currency from '../../../lib/currency.jsx'
import ExpenseUtil from '../../../lib/expenseutil.js'
import ExpenseForm from '../../../expense/form/expense_form/expense_form.jsx'
import PageHeader from '../../../theme/layout/page_header/page_header.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'
import Util from '../../../lib/util.jsx'
import DateUtil from '../../../lib/dateutil.jsx'
import Acl from '../../../lib/acl.jsx'
import PopupPhaseAction from '../../popup/popup_phaseaction/popup_phaseaction.jsx'
import { ProjectsIcon } from '../../../common/layout/menu_icons/projects.jsx'

import { getVatRateSelectOptionsFromArray } from '../../../lib/vat_rates_select_options.js'
import { getVatRatesForCountry } from '../../../../shared/util/vat'

class BaseProjectView extends PageDetail {
  constructor (props, ident, sidebarIdent, helpIdent) {
    super(props, ident, sidebarIdent, helpIdent)

    this.data = {
      project: this._getProjectFromCache(this.props.projectId),
      stat: this._getOverviewStatFromCache(this.props.projectId)
    }
    this._references = {
      // Create using this.createCommonReference() from derived page
      sidebarTask: React.createRef()
    }
    this.createCommonReference(this._references)

    this._tagSuggestions = []
    this.ident = ident
  }

  /* Event Handlers */
  _onChangePage (pageIdent) {
    app.changePage(pageIdent, this.props.projectId)
  }

  _getDataPhases (callback) {
    const budgetItemParams = {
      query: {
        projectId: this.props.projectId,
        spentAmount: { $gt: 0 },
        $sort: { orderNo: 1 }
      }
    }
    app
      .service('/api/project/budgetitem')
      .find(budgetItemParams)
      .then(result => {
        const itemsHasSpent = result.data
        let phases = []
        if (this.data.project) {
          const projectObj = _.cloneDeep(this.data.project)
          phases = projectObj.budget.phases || []
          phases.map((phase, phaseIdx) => {
            phase.disableEstimate = false
            const phaseNo = phaseIdx + 1

            itemsHasSpent.map(item => {
              if (item.phaseNo === phaseNo) {
                phase.disableEstimate = true
              }
            })
          })
        }

        callback(phases)
      })
      .catch(err => {
        app.debug('PageProjectViewPlanner: _getBudgetItems Error', err)
        callback([])
      })
  }

  _onChangeStatus (item, callback) {
    if (item.ident === 'complete-allphases') {
      this._completeAllPhases()
    } else if (item.ident === 'close-allphases') {
      this._closeAllPhases()
    } else {
      const status = item.ident === 'reopen' ? 'completed' : item.ident
      this._updateProjectStatus({ status: status })
    }

    callback(null)
  }

  _completeAllPhases () {
    if (this.data.project.budget) {
      this.data.project.budget.phases.map(phase => {
        phase.status = 'completed'
      })

      this._updateProjectStatus({ budget: this.data.project.budget })
    }
  }

  _closeAllPhases () {
    if (this.data.project.budget) {
      this.data.project.budget.phases.map(phase => {
        phase.status = 'closed'
      })

      this._updateProjectStatus({ budget: this.data.project.budget })
    }
  }

  _updateProjectStatus (updateObj) {
    app
      .service('/api/project')
      .patch(this.props.projectId, updateObj)
      .then(_teamRes => {
        this._reloadData()
      })
      .catch(teamErr => {
        app.debug('PageProjectViewBudget: _onChangeStatus err', teamErr)
      })
  }

  _onChangeFilter (_filter) {
    this.forceUpdate()
  }

  _initQuickbookItems () {
    /* Load quickbook options for expense */
    ExpenseUtil.initQuickbookItems()
  }

  onEditProject () {
    const project = this.data.project
    const formRef = this._references.projectform.current

    if (project && this.props.projectId && formRef) {
      const projectSetting = app.projectSetting

      if (
        this.data.showedit &&
        projectSetting.useProjectNo &&
        projectSetting.isGenerated === false
      ) {
        this._getProjectNumber(false, project.contactId)
          .then(projectNumber => {
            formRef.show(
              project,
              '',
              projectNumber,
              projectSetting.useProjectNo,
              projectSetting.isGenerated
            )
          })
          .catch(err => {
            app.debug('Page onAdd - nextProjectNumber - Error', err)
          })
      } else {
        formRef.show(
          project,
          project.projectNumber || '',
          '',
          projectSetting.useProjectNo || false,
          projectSetting.isGenerated || false
        )
      }
    }
  }

  _getProjectNumber (isGenerated, contactId) {
    const projectParams = {
      query: {
        type: isGenerated ? 'nextProjectNumber' : 'previousProjectNumber',
        contactId: contactId
      }
    }

    return app.service('/api/page/project').get(null, projectParams)
  }

  createCommonReference (reference) {
    reference.refPageHeaderProjectView = React.createRef()
    reference.projectform = React.createRef()
    reference.expenseForm = React.createRef()
    reference.phaseStatus = React.createRef()
  }

  _onCloseForm () {
    if (this._references.projectform.current) {
      this._references.projectform.current.hide()
    }
  }

  _getProjectFromCache (projectId) {
    const project = app.getAsObject('project')
    if (project && project.id === projectId) {
      return project
    }

    return null
  }

  _getOverviewStatFromCache (projectId) {
    const project = app.getAsObject('project')
    if (project && project.id === projectId) {
      return app.getAsObject('overviewstat') || null
    }

    return null
  }

  _setProjectToCache (project) {
    app.setAsObject('project', project)
  }

  _setOverviewStatToCache (stat) {
    app.setAsObject('overviewstat', stat)
  }

  _initProjectCommonEvents () {
    this._eventHandlers.headerStatCreated = this._eventStatReload.bind(
      this,
      'ProjectStat',
      'Created'
    )

    const projectStatService = app.service('/api/project/stat')
    projectStatService.on('created', this._eventHandlers.headerStatCreated)
  }

  _releaseProjectCommonEvents () {
    const projectStatService = app.service('/api/project/stat')
    projectStatService.off('created', this._eventHandlers.headerStatCreated)
  }

  /* Private function */
  _getData () {}

  _reloadData () {
    this._getData()
  }

  _eventStatReload () {
    app.debug('-- Event - BaseProjectView - Stat - Created --')

    const findParams = {
      query: {
        projectId: this.props.projectId
      }
    }

    return app
      .service('/api/project/stat')
      .find(findParams)
      .then(statRes => {
        if (statRes.data.length > 0) {
          this.data.stat = statRes.data[0]
          this._setOverviewStatToCache(this.data.stat)

          this.setState({ dataDate: new Date() })
        }
      })
      .catch(err => {
        app.debug('BaseProjectView: _eventStatReload err', err)
      })
  }

  _getDataCommon (callback) {
    const params = {
      query: {
        $client: {
          subObjects: {
            contact: ['company', 'rateCards', 'rateMarkup', 'rate']
          }
        }
      }
    }

    app
      .service('/api/project')
      .get(this.props.projectId, params)
      .then(result => {
        this.data.project = result
        this._setProjectToCache(this.data.project)

        TagUtil.getProjectTags(tags => {
          this._tagSuggestions = tags
          callback(null)
        })
      })
      .catch(err => {
        app.debug('BaseProjectView: _getDataProjectView err', err)
        callback(err)
      })
  }

  _onSubmitProject (project, id, newClientName) {
    if (newClientName) {
      this._commonCreateContact(newClientName, contactId => {
        if (contactId) {
          this._updateProject(project, id, contactId)
        }
      })
    } else {
      this._updateProject(project, id, null)
    }
  }

  _onChangePhaseStatus (phaseIdx, statusObj, closeDate) {
    if (this.data.project.budget && this.props.projectId) {
      if (this._references.phaseStatus.current) {
        this._references.phaseStatus.current.hide()
      }

      const updateObj = {
        budget: this.data.project.budget
      }

      if (updateObj.budget.phases[phaseIdx]) {
        updateObj.budget.phases[phaseIdx].status = statusObj.status
      }

      if (closeDate) {
        updateObj.closeDate = closeDate
      }
      app
        .service('/api/project')
        .patch(this.props.projectId, updateObj)
        .then(_teamRes => {
          this._reloadData()
        })
        .catch(teamErr => {
          app.debug('PageProjectViewBudget: _onChangeStatus err', teamErr)
        })
    }
  }

  _onChangeProjectStatus (projectStatus, closeDate) {
    if (this.data.project.budget && this.props.projectId) {
      if (this._references.phaseStatus.current) {
        this._references.phaseStatus.current.hide()
      }
      const budget = _.cloneDeep(this.data.project.budget)

      const updateObj = {
        status: projectStatus,
        budget: budget
      }

      if (updateObj.budget && updateObj.budget.phases.length > 0) {
        updateObj.budget.phases.map(phase => {
          if (phase.groups) {
            delete phase.groups
          }

          phase.status = projectStatus
        })
      }

      if (closeDate && projectStatus === 'closed') {
        updateObj.closeDate = closeDate
      }

      this._updateProjectStatus(updateObj)
    }
  }

  _updateProject (project, id, contactId) {
    /** Currently not needed - since project status is hidden in form
    const currStatus = this.data.project.status;
    let matches = 0;

    if (currStatus === 'estimate') {
      matches = _.indexOf(['active', 'completed', 'closed', 'estimate'], project.status);
    } else if (currStatus === 'active') {
      matches = _.indexOf(['completed', 'closed', 'active', 'estimate'], project.status);
    } else if (currStatus === 'completed') {
      matches = _.indexOf(['closed', 'completed', 'active'], project.status);
    } else if (currStatus === 'closed') {
      matches = _.indexOf(['reopen', 'closed'], project.status);
    }

    if (matches < 0) {
      this.showError(`Status change from ${currStatus.toUpperCase()} to
         ${project.status.toUpperCase()} not allowed`);
    } else { ***/
    project.teamId = app.getTeamId()
    project.createdById = app.loggedInId
    if (contactId) {
      project.contactId = contactId
    }
    app
      .service('/api/project')
      .patch(id, project)
      .then(_prjRes => {
        if (this._references.projectform.current) {
          this._references.projectform.current.hide()
        }
        this._reloadData()
      })
      .catch(prjErr => {
        app.debug('PageProjectView: _updateProject Error', prjErr)

        if (prjErr.message === 'Project name already exists for this client') {
          if (this._references.projectform.current) {
            this._references.projectform.current.clearProjectName()
          }
          this.showError('project.form.msgProjectExists')
        } else if (prjErr.message === 'Project number already exists') {
          this.showError('project.form.msgProjectNoExists')
        } else {
          this.showError('project.form.msgErrorUpdateProject')
        }
      })
    /** } **/
  }

  _onSearchContact (keyword, callback) {
    if (keyword) {
      const pattern = keyword
        .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
        .replace(/\$/g, '$$$$')
      const params = {
        query: {
          company: { $regex: pattern, $options: 'i' },
          $select: ['_id', 'company', 'archived'],
          $limit: 15
        }
      }

      app
        .service('/api/contact')
        .find(params)
        .then(result => {
          if (result.data) {
            const options = result.data.map(obj => ({
              label: obj.company,
              value: obj.id,
              bArchived: obj.archived
            }))
            callback(options)
          } else {
            callback([])
          }
        })
        .catch(err => {
          app.debug('PageContact: getDataContact err', err)
          callback([])
        })
    } else {
      callback([])
    }
  }

  _getHeaderFilter () {
    return null
  }

  _renderExpenseForm () {
    const { team, contactId, project } = this.data
    const contactName =
      project && project.contact ? project.contact.company : ''
    const vatLevels =
      team && team.profile ? getVatRateSelectOptionsFromArray(getVatRatesForCountry(this.data.team.profile.country)) : []

    return (
      <ExpenseForm
        ref={this._references.expenseForm}
        onSubmit={this._onSubmitExpense.bind(this)}
        contactsearch={this._onSearchExpContact.bind(this)}
        onValidateInvoiceNo={ExpenseUtil.validateInvoiceNo}
        showError={this.showError.bind(this)}
        getProjects={ExpenseUtil.getProjects}
        getContactBudgetItems={ExpenseUtil.getContactBudgetItems}
        onAddBudgetExpense={ExpenseUtil.onAddBudgetExpense}
        onSelectBudgetExpense={ExpenseUtil.onSelectBudgetExpense}
        onSearchBudgetItem={ExpenseUtil.getProjectBudgetItems}
        onClose={this._onCloseExpenseForm.bind(this)}
        onCreateContact={ExpenseUtil.onCreateContact}
        attachment={this.data.team.integration.isAttached}
        validateExpenseTotalWithPO={ExpenseUtil.validateExpenseTotalWithPO}
        vatLevels={vatLevels}
        contactId={contactId}
        projectId={this.props.projectId}
        defaultVat={app.getDefaultVat()}
        contactName={contactName}
        showVat={app.isVatSupportedInCountry(app.teamCountry)}
        isQuickbookConnected={app.isQuickbookConnected()}
        getQuickbookItems={ExpenseUtil.getQuickbookItems}
        reloadQuickbookItems={ExpenseUtil.reloadQuickbookItems}
      />
    )
  }

  _onSearchExpContact (keyword, callback) {
    this._onCommonFilterContactSearch(keyword, callback)
  }

  _onCloseExpenseForm (noBudgetItem) {
    if (noBudgetItem > 0) {
      this._onAfterSaveExpense()
    }

    if (this._references.expenseForm.current) {
      this._references.expenseForm.current.hide()
    }
  }

  _onSubmitExpense (
    expenseObj,
    expenseId,
    items,
    deletedItemIds,
    fileObj,
    bDeleteFile,
    closePoIds,
    newContactData,
    callback
  ) {
    expenseObj.createdById = app.loggedInId
    const bSendQuickbook = expenseObj.bSendQuickbook

    if (expenseId) {
      ExpenseUtil.updateExpense(
        expenseObj,
        expenseId,
        items,
        deletedItemIds,
        fileObj,
        bDeleteFile,
        closePoIds,
        newContactData,
        updateErr => {
          if (updateErr) {
            app.debug('PagePurchaseOrder updateErr', updateErr)
            if (updateErr.message === 'Invoice number already exists') {
              this.showError('expense.list.errorInvoiceNoAlreadyExists')
            } else if (updateErr.code >= 501 || updateErr.code <= 510) {
              this.showError(`expense.list.errorMsg${updateErr.code}`)
            } else {
              this.showError('expense.list.errorUpdateMsg')
            }
            callback(updateErr)
          } else {
            this._onAfterSave(false, bSendQuickbook, null, callback)
          }
        }
      )
    } else {
      ExpenseUtil.createExpense(
        expenseObj,
        items,
        deletedItemIds,
        fileObj,
        bDeleteFile,
        closePoIds,
        newContactData,
        (createErr, createRes) => {
          if (createErr) {
            app.debug('PagePurchaseOrder createErr', createErr)
            if (createErr.message === 'Invoice number already exists') {
              this.showError('expense.list.errorInvoiceNoAlreadyExists')
            } else if (createErr.code >= 501 || createErr.code <= 510) {
              this.showError(`expense.list.errorMsg${createErr.code}`)
            } else {
              this.showError('expense.list.errorCreateMsg')
            }
            callback(createErr)
          } else {
            this._onAfterSave(true, bSendQuickbook, createRes.expense, callback)
          }
        }
      )
    }
    this._getData()
  }

  _onAfterSave (bCreateExpense, bSendQuickbook, expense, callback) {
    // If Quickbooks is not connected, close the form immediately
    // If expense is already sent to Quickbooks, close the form immediately
    if (!app.isQuickbookConnected() || !bSendQuickbook) {
      callback(null)
      this._onAfterSaveExpense()
    } else {
      // If Quickbooks is connected, open form in edit mode,
      // and wait until the Quickbooks response comes
      if (bCreateExpense) {
        if (expense && expense.id) {
          ExpenseUtil.getExpenseDetails(expense.id, (expenseObj, items) => {
            if (expenseObj) {
              if (this._references.expenseForm.current) {
                this._references.expenseForm.current.show(
                  expenseObj,
                  true,
                  items
                )
              }
            }
          })
        }
      }
    }
  }

  /* Header Functions - START */
  renderHeader () {
    const { project, stat } = this.data
    if (!project) {
      return null
    }

    const pageState = this.data.pageState || {}
    const status = project.status
    const actions = [
      {
        ident: 'status',
        type: 'button',
        title: IntlUtil.convertMessage(Util.getTranslatedStatus(status)),
        className: `headerstatus project_${status.toLowerCase()}`,
        onClick: this._onHeaderClickStatus.bind(this)
      },
      {
        ident: 'edit',
        icon: 'edit',
        tooltip: 'common.list.tipEditDetails',
        tooltipPosition: 'bottom',
        onClick: this.onEditProject.bind(this)
      },
      {
        ident: 'daterange',
        type: 'info',
        title: this._formatDateRange(project.startDate, project.endDate)
      }
    ]

    const tabs = [
      {
        ident: 'projectview-overview',
        title: 'project.view.overView',
        showFavourite: true
      },
      {
        ident: 'projectview-budget',
        title: 'project.view.budget',
        showFavourite: true
      }
    ]

    if (Acl.isPlannerAllowed()) {
      tabs.push(
        {
          ident: 'projectview-task',
          title: 'project.view.task',
          showFavourite: true
        },
        {
          ident: 'projectview-planner',
          title: 'project.view.planner',
          showFavourite: true
        }
      )
    }

    tabs.push(
      {
        ident: 'projectview-hour',
        title: 'project.view.hours',
        showFavourite: true
      },
      {
        ident: 'projectview-team',
        title: 'project.view.team',
        showFavourite: true
      }
    )

    const config = app.getConfig()
    if (config && config.instanceEnv !== 'production') {
      tabs.push({
        ident: 'projectview-budgetv2',
        title: 'Budget V2',
        showFavourite: true
      })
    }

    const invoiceSubItems = []
    let statInfos = []

    // If project is not closed Actual margin = (selling price or invoiced) - Expense budget - Overspent
    // If closed Realized margin = invoiced - booked expenses - remaining PO

    if (stat) {
      if (status === 'closed') {
        invoiceSubItems.push({
          title: 'project.view.subTooltipInvoicedTotal',
          value: stat.invoiceInfo.invoicedTotal,
          isPrice: true,
          suffixText: ' - '
        })

        invoiceSubItems.push({
          title: 'project.view.subTooltipExpenseBooked',
          value: stat.expenseInfo.expensesBooked,
          isPrice: true,
          suffixText: ' - '
        })

        invoiceSubItems.push({
          title: 'project.view.subTooltipRemainingPo',
          value: stat.expenseInfo.openPoAmount,
          isPrice: true,
          suffixText: ' = '
        })

        invoiceSubItems.push({
          title: 'project.view.subTooltipRealizedMargin',
          value: stat.invoiceInfo.realizedMargin,
          isPrice: true,
          align: 'center'
        })
      } else {
        if (stat.invoiceInfo.invoicedTotal >= stat.invoiceInfo.sellingTotal) {
          invoiceSubItems.push({
            title: 'project.view.subTooltipInvoicedTotal',
            value: stat.invoiceInfo.invoicedTotal,
            isPrice: true,
            suffixText: ' - '
          })
        } else {
          invoiceSubItems.push({
            title: 'project.view.subTooltipBudgetSellingFor',
            value: stat.invoiceInfo.sellingTotal,
            isPrice: true,
            suffixText: ' - '
          })
        }

        invoiceSubItems.push({
          title: 'project.view.subTooltipExpenseBudget',
          value: stat.expenseInfo.expensesTotal,
          isPrice: true,
          suffixText: ' - '
        })

        invoiceSubItems.push({
          title: 'project.view.subTooltipInvoiceOverspent',
          value: stat.timeInfo.overSpent + stat.expenseInfo.overSpent,
          isPrice: true,
          suffixText: ' = '
        })

        invoiceSubItems.push({
          title: 'project.view.subTooltipActualMargin',
          value: stat.invoiceInfo.actualMargin,
          isPrice: true,
          align: 'center'
        })
      }

      const progressTime =
        stat.timeInfo.hoursTrackedTotal && stat.timeInfo.hoursTotal
          ? Math.round(
              (stat.timeInfo.hoursTrackedTotal * 100) / stat.timeInfo.hoursTotal
            )
          : 0
      const progressExpense =
        stat.expenseInfo.expensesBooked && stat.expenseInfo.expensesTotal
          ? Math.round(
              (stat.expenseInfo.expensesBooked * 100) /
                stat.expenseInfo.expensesTotal
            )
          : 0
      const progressInvoice =
        stat.invoiceInfo.sellingTotal && stat.invoiceInfo.invoicedTotal
          ? Math.round(
              (stat.invoiceInfo.invoicedTotal * 100) /
                stat.invoiceInfo.sellingTotal
            )
          : 0

      const invoice = stat?.invoiceInfo?.sellingTotal || 0
      const expenses = (stat?.expenseInfo?.expensesTotal || 0) + (stat?.timeInfo?.overSpent || 0) + (stat?.expenseInfo?.overSpent || 0)
      // can't divide 0 by 0 so divide it by 1
      const percentage = parseFloat(((invoice - expenses) / (invoice || 1) * 100).toFixed(0))

      statInfos = [
        {
          title: `${IntlUtil.convertMessage('project.view.hoursTracked')}`,
          value: stat.timeInfo.hoursTrackedTotal || 0,
          isMultilingual: false,
          subtitle: `${IntlUtil.convertMessage(
            'project.view.timeBudget'
          )} ${currency.formatSymbol(stat.timeInfo.hoursTotal)}`,
          isPrice: true,
          progress: progressTime,
          showWarningColorInProgressBar: true,
          progressType:
            progressTime === 100
              ? 'success'
              : progressTime > 100
              ? 'error'
              : 'normal',
          tooltipInfos: [
            {
              type: 'row',
              hasSpliter: false,
              items: [
                {
                  title: 'project.view.tooltipBudgetOverspent',
                  value:
                    stat.timeInfo.overSpent > 0 ? stat.timeInfo.overSpent : 0,
                  showWarning:
                    stat.timeInfo.hoursTrackedTotal > stat.timeInfo.hoursTotal,
                  isPrice: true,
                  align: 'right'
                }
              ]
            },
            {
              type: 'row',
              hasSpliter: false,
              items: [
                {
                  title: 'project.view.tooltipBudgetInitialBudget',
                  value: stat.timeInfo.initialBudget,
                  showWarning:
                    stat.timeInfo.hoursTotal > stat.timeInfo.initialBudget,
                  isPrice: true,
                  align: 'right'
                }
              ]
            }
          ]
        },
        {
          title: 'project.view.expensesTracked',
          value: stat.expenseInfo.expensesBooked || 0,
          subtitle: `${IntlUtil.convertMessage(
            'project.view.expensesBudget'
          )} ${currency.formatSymbol(stat.expenseInfo.expensesTotal)}`,
          isPrice: true,
          progress: progressExpense,
          showWarningColorInProgressBar: true,
          progressType:
            progressExpense === 100
              ? 'success'
              : progressExpense > 100
              ? 'error'
              : 'normal',
          tooltipInfos: [
            {
              type: 'row',
              hasSpliter: false,
              items: [
                {
                  title: 'project.view.tooltipExpenseOpenPoAmount',
                  value: stat.expenseInfo.openPoAmount,
                  showWarning: false,
                  isPrice: true,
                  align: 'right'
                },
                {
                  title: 'project.view.tooltipExpenseOverspent',
                  value:
                    stat.expenseInfo.overSpent > 0
                      ? stat.expenseInfo.overSpent
                      : 0,
                  showWarning: stat.expenseInfo.overSpent > 0,
                  isPrice: true,
                  align: 'right'
                }
              ]
            },
            {
              type: 'row',
              hasSpliter: false,
              items: [
                {
                  title: 'project.view.tooltipExpenseInitialBudget',
                  value: stat.expenseInfo.initialBudget,
                  showWarning:
                    stat.expenseInfo.expensesTotal >
                    stat.expenseInfo.initialBudget,
                  isPrice: true,
                  align: 'right'
                }
              ]
            }
          ]
        },
        {
          title: 'project.view.totalInvoiced',
          value: stat.invoiceInfo.invoicedTotal || 0,
          subtitle: `${IntlUtil.convertMessage(
            'project.view.sellingFor'
          )} ${currency.formatSymbol(stat.invoiceInfo.sellingTotal)}`,
          isPrice: true,
          progress: progressInvoice,
          showWarningColorInProgressBar: false,
          tooltipInfos: [
            {
              type: 'row',
              hasSpliter: false,
              items: invoiceSubItems
            },
            {
              type: 'row',
              hasSpliter: true,
              splitterWidth: 70,
              alignRow: 'left',
              items: [
                {
                  title: 'project.view.tooltipInvoiceApprovedEstimates',
                  value: stat.invoiceInfo.approvedEstimates,
                  showWarning: false,
                  isPrice: true
                },
                {
                  title: 'project.view.tooltipInvoiceOverdues',
                  value: stat.invoiceInfo.overdueInvoices || 0,
                  showWarning: false,
                  isPrice: true
                },
                {
                  title: 'project.view.tooltipInvoiceBudgetedMargin',
                  value: stat.invoiceInfo.budgetedMargin || 0,
                  showWarning: false,
                  isPrice: true
                },
                {
                  align: 'center',
                  className: 'is-element',
                  value: (
                    <ThemeProvider theme={lightTheme}>
                      <RadialProgressBar progress={percentage} radius={40} progressRadius={15} />
                    </ThemeProvider>
                  )
                }
              ]
            }
          ]
        }
      ]
    }

    const contentFilters = []
    const activeContentFilters = []
    let isGridActive = pageState.type === 'grid'
    let bPlannerDetailPage = false
    if (
      this.ident === 'projectview-planner' ||
      this.ident === 'projectview-task'
    ) {
      bPlannerDetailPage = true
    }

    if (this.ident === 'projectview-overview') {
      contentFilters.push(
        {
          ident: 'estimate',
          title: 'project.view.estimates'
        },
        {
          ident: 'expense',
          title: 'project.view.expenses'
        },
        {
          ident: 'purchaseorder',
          title: 'project.view.pos'
        },
        {
          ident: 'invoice',
          title: 'project.view.invoices'
        },
        {
          ident: 'all',
          title: 'project.view.all'
        }
      )

      activeContentFilters.push(pageState.activityFilterType)
    } else if (this.ident === 'projectview-budget') {
      contentFilters.push(
        {
          ident: 'time',
          title: 'project.view.time'
        },
        {
          ident: 'expense',
          title: 'project.view.expenses'
        },
        {
          ident: 'all',
          title: 'project.view.all'
        }
      )

      activeContentFilters.push(pageState.filterType)
    } else if (this.ident === 'projectview-hour') {
      contentFilters.push(
        {
          ident: 'people',
          title: 'project.view.people'
        },
        {
          ident: 'activities',
          title: 'project.view.activities'
        }
      )

      activeContentFilters.push(this.state.contentType)
      isGridActive = this.state.filterType === 'grid'
    }

    return (
      <div className='baser-projectviewheader'>
        <PageHeader
          ref={this._references.refPageHeaderProjectView}
          icon={<ProjectsIcon width={25} />}
          projectSetting={app.projectSetting}
          projectNumber={project.projectNumber}
          title={project.title}
          subtitle={
            project.contact ? project.contact.company.toUpperCase() : ''
          }
          isTitleMultilingual={false}
          tabs={tabs}
          activeTab={this.ident}
          favoriteTab={
            this._getUserFavoriteTab('projectView') || 'projectview-overview'
          }
          statInfos={bPlannerDetailPage ? [] : statInfos}
          actions={actions}
          alignActions='left'
          contentFilters={contentFilters}
          activeContentFilters={activeContentFilters}
          showGridList={
            ['projectview-budget', 'projectview-hour'].indexOf(this.ident) >= 0
          }
          isGridActive={isGridActive}
          onTabChange={this._onHeaderTabChange.bind(this)}
          onContentFilterChange={this._onHeaderContentFilter.bind(this)}
          onGridListToggle={this._onHeaderGridListToggle.bind(this)}
          onToggleFavorite={this._onHeaderToggleFavorite.bind(this)}
          onClickSubtitle={this._onHeaderOpenContact}
        />
        <ProjectForm
          ref={this._references.projectform}
          withoutContext={this.ident === 'projectview-budget'}
          prefixCurrency={currency.symbol()}
          teamCountry={app.teamCountry}
          rateMarkup={app.getDefaulRateMarkup()}
          tagSuggestions={this._tagSuggestions}
          defaultCountry={this._defaultCountry}
          contactSearch={this._onSearchContact.bind(this)}
          onClose={this._onCloseForm.bind(this)}
          onSubmit={this._onSubmitProject.bind(this)}
          getProjectNumber={this._getProjectNumber.bind(this)}
        />
        <PopupPhaseAction
          ref={this._references.phaseStatus}
          closeDate={project.closeDate}
          container={this._references.refPageHeaderProjectView.current}
          projectStatus={status}
          onChangeProjectStatus={this._onChangeProjectStatus.bind(this)}
          onChangePhaseStatus={this._onChangePhaseStatus.bind(this)}
        />
      </div>
    )
  }

  _formatDateRange (startDate, endDate) {
    if (startDate && endDate) {
      if (DateUtil.isDateEqual(startDate, endDate)) {
        return `${DateUtil.getDisplayDate(startDate)}`
      }

      return `${DateUtil.getDisplayDate(startDate)} - ${DateUtil.getDisplayDate(
        endDate
      )}`
    } else if (startDate && !endDate) {
      return `${DateUtil.getDisplayDate(
        startDate
      )} - <span class="infoinfinity">&infin;</span>`
    } else if (!startDate && endDate) {
      return `<span class="infoinfinity">&infin;</span> - ${DateUtil.getDisplayDate(
        endDate
      )}`
    }

    return IntlUtil.convertMessage('project.viewBudget.noDateSetLabel')
  }

  _onHeaderTabChange (tab) {
    app.changePage(tab.ident, this.props.projectId)
  }

  _onHeaderToggleFavorite (tab) {
    this._updateUserStateFavorite('projectView', tab.ident)
    this.setState({ dataDate: new Date() })
  }

  _onHeaderContentFilter (_option) {}

  _onHeaderGridListToggle (_isGridActive) {}

  _onHeaderOpenContact = () => {
    if (this.data.project && this.data.project.contactId) {
      app.changePage('contact-view', this.data.project.contactId)
    }
  }

  _onHeaderClickStatus () {
    if (this._references.phaseStatus.current) {
      this._getDataPhases(phases => {
        if (phases.length > 0) {
          this._references.phaseStatus.current.show(phases)
        }
      })

      this.setState({ dataDate: new Date() })
    }
  }
  /* Header Functions - End */

  _renderSidebar () {
    return this.renderSidebar()
  }

  render () {
    return this.renderDefaultPageContent()
  }
}

export default BaseProjectView
